import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useEntityAction } from "@/lib/mutations";
import { Spin } from "@/components/ui";
import type { DriverGuide, DriverInvite } from "@/lib/types";
import { formatCompanyDateTime } from "@/lib/time";

/** "Invite to Telegram" / "Send test" for one driver row — the Phase 13.1
 * linking status + actions (there is no separate driver detail page, so this
 * lives inline in the drivers table). */
export function DriverTelegramActions({
  driver,
  canWrite,
}: {
  driver: DriverGuide;
  canWrite: boolean;
}) {
  const { t } = useTranslation();
  const [invite, setInvite] = useState<DriverInvite | null>(null);
  const [testResult, setTestResult] = useState<"sent" | null>(null);
  // both show their own result on the row (the invite link / "test queued"),
  // so no extra "Saved" toast
  const inviteAction = useEntityAction([["drivers-guides"]], {
    inlineError: true,
    successMessage: false,
    onSuccess: () => setTestResult(null),
  });
  const testAction = useEntityAction([["drivers-guides"]], {
    inlineError: true,
    successMessage: false,
  });

  const linked = driver.telegram_user_id != null;

  return (
    <div className="flex flex-col gap-1 text-xs">
      <span
        className={
          "inline-block w-fit rounded-full px-2 py-0.5 " +
          (linked
            ? "bg-emerald-500/15 text-emerald-700"
            : "bg-olive-700 text-sand-muted")
        }
      >
        {linked ? t("drivers.telegramLinked") : t("drivers.telegramNotLinked")}
      </span>
      {canWrite && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            className="text-gold hover:underline disabled:opacity-50"
            disabled={inviteAction.isPending}
            aria-busy={inviteAction.isPending || undefined}
            onClick={() => {
              setInvite(null);
              inviteAction.mutate(() =>
                api.post<DriverInvite>(`/drivers-guides/${driver.id}/invite`).then(
                  (res) => {
                    setInvite(res);
                    return res;
                  },
                ),
              );
            }}
          >
            {inviteAction.isPending && <Spin className="mr-1.5" />}
            {t("drivers.invite")}
          </button>
          {linked && (
            <button
              className="text-gold hover:underline disabled:opacity-50"
              disabled={testAction.isPending}
              aria-busy={testAction.isPending || undefined}
              onClick={() =>
                testAction.mutate(
                  () => api.post(`/drivers-guides/${driver.id}/test-message`),
                  { onSuccess: () => setTestResult("sent") },
                )
              }
            >
              {testAction.isPending && <Spin className="mr-1.5" />}
              {t("drivers.sendTest")}
            </button>
          )}
        </div>
      )}
      {invite && (
        <div className="max-w-xs break-all rounded-md border border-olive-600 bg-olive-900 p-2">
          <a
            className="text-sand hover:text-gold"
            href={invite.link}
            target="_blank"
            rel="noreferrer"
          >
            {invite.link}
          </a>
          <div className="mt-1 text-sand-muted">
            {t("drivers.inviteExpires")}:{" "}
            {formatCompanyDateTime(invite.expires_at)}
          </div>
        </div>
      )}
      {testResult === "sent" && (
        <span className="text-sand-muted">{t("drivers.testQueued")}</span>
      )}
      {(inviteAction.isError || testAction.isError) && (
        <span className="text-red-700">
          {((inviteAction.error || testAction.error) as Error).message}
        </span>
      )}
    </div>
  );
}
